import styled, { createGlobalStyle, css } from 'styled-components'
import { Button } from '@stokr/components-library'
import { InfoCircleIcon } from 'components/ui/icons'
import { stoColor, stoMedia, stoRadius, stoSpace, stoType } from 'constants/stoTheme'

/** Below desktop (Figma 3582:124417 / 3573:90075) KeyActions is a bottom sheet behind a sticky bar. */
const MOBILE = stoMedia.belowDesktop
const SHEET_MOTION = '300ms cubic-bezier(0.4, 0, 0.2, 1)'
/** The sticky bar: 16px padding around a 48px button, plus its top border. */
const MOBILE_BAR_HEIGHT = '81px'

/**
 * Not wrapped in the shared `Section` — no title/divider header here, the tab list IS the
 * header — so this owns the anchor id `Section` would otherwise supply. On mobile the same
 * element is the bottom sheet, so there is only ever one KeyActions on the page.
 */
export const Root = styled.div<{ $open: boolean; $expanded: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${stoSpace[4]};
  width: 100%;
  scroll-margin-top: 96px;

  /* Focused programmatically when the sheet opens; the sheet itself is the indicator. */
  &:focus {
    outline: none;
  }

  ${MOBILE} {
    position: fixed;
    right: 0;
    bottom: 0;
    left: 0;
    /* Above the site header (1000); page modals close the sheet before they open. */
    z-index: 1002;
    /* Content taller than the screen takes it all, up to the top; shorter content stays a bottom sheet. */
    max-height: 100vh;
    max-height: 100dvh;
    overflow-y: auto;
    /* Its own scroll ends here instead of carrying on to the page behind. */
    overscroll-behavior: contain;
    /* No bottom padding here: WebKit leaves it out of the scroll range (see SheetBody). */
    padding: ${stoSpace[5]} ${stoSpace[6]} 0;
    gap: ${stoSpace[5]};
    background: ${stoColor.surface};
    border: 1px solid ${stoColor.borderSubtle};
    border-bottom: none;
    border-radius: ${stoRadius.lg} ${stoRadius.lg} 0 0;
    transform: translateY(${({ $open }) => ($open ? '0' : '100%')});
    /* Hidden when closed, so its fields are not reachable by keyboard off screen. */
    visibility: ${({ $open }) => ($open ? 'visible' : 'hidden')};
    transition:
      transform ${SHEET_MOTION},
      visibility 0s ${({ $open }) => ($open ? '0s' : '300ms')};

    @media (prefers-reduced-motion: reduce) {
      transition: none;
    }

    /* Typing on a phone: the whole screen, over the site header (useSheetExpansion). */
    ${({ $expanded }) =>
      $expanded &&
      `
      top: 0;
      max-height: none;
      border-radius: 0;
    `}
  }
`

/** "INVEST, TRADE OR REDEEM" with the close chevron; only in the mobile sheet. */
export const SheetHeader = styled.div`
  display: none;

  ${MOBILE} {
    ${stoType.columnLabel};
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${stoSpace[2]};
    color: ${stoColor.textSecondary};
  }
`

export const SheetClose = styled.button`
  display: inline-flex;
  padding: ${stoSpace[1]};
  border: none;
  background: none;
  color: ${stoColor.textPrimary};
  cursor: pointer;
`

/** Mobile only: the cards in the sheet, 16px apart. */
export const SheetBody = styled.div`
  display: contents;

  ${MOBILE} {
    display: flex;
    flex-direction: column;
    gap: ${stoSpace[4]};
    /* The sheet's bottom padding: inside the content, so a full-height sheet scrolls to it on iOS too. */
    padding-bottom: ${stoSpace[6]};
  }
`

export const Backdrop = styled.div<{ $open: boolean }>`
  display: none;

  ${MOBILE} {
    display: block;
    position: fixed;
    inset: 0;
    z-index: 1001;
    background: rgba(0, 0, 0, 0.4);
    /* Swiping the dimmed page does nothing; tapping still closes the sheet. */
    touch-action: none;
    opacity: ${({ $open }) => ($open ? 1 : 0)};
    pointer-events: ${({ $open }) => ($open ? 'auto' : 'none')};
    transition: opacity ${SHEET_MOTION};
  }
`

/**
 * Mobile only: follows the active panel's measured height (`--panel-height`, set from a
 * ResizeObserver), so switching tabs grows or shrinks the sheet instead of jumping.
 */
export const PanelViewport = styled.div`
  ${MOBILE} {
    height: var(--panel-height, auto);
    overflow: hidden;
    transition: height ${SHEET_MOTION};

    @media (prefers-reduced-motion: reduce) {
      transition: none;
    }
  }
`

/**
 * Room after the whole page (site footer included) for the sticky bar, so it never covers content.
 * On `#root`, not `body`: the body is fixed at the viewport's height, so its padding never reached the page's end.
 */
export const MobileBarPageSpace = createGlobalStyle`
  ${MOBILE} {
    #root {
      padding-bottom: ${MOBILE_BAR_HEIGHT};
    }
  }
`

/** Figma "bottom bar closed": always on screen below desktop. */
export const MobileBar = styled.div`
  display: none;

  ${MOBILE} {
    display: block;
    position: fixed;
    right: 0;
    bottom: 0;
    left: 0;
    /* Above the site footer (15), which would otherwise slide over it at the page's end; below
       the site header (900), so the open burger menu and its Log in / Sign up bar cover it. */
    z-index: 16;
    padding: ${stoSpace[4]} ${stoSpace[6]};
    background: ${stoColor.surface};
    border-top: 1px solid ${stoColor.borderSubtle};
  }
`

/** Figma: heavier shadow than a plain Section card — this one floats above the page. */
export const Card = styled.div`
  display: flex;
  flex-direction: column;
  background: ${stoColor.surface};
  border: 1px solid ${stoColor.borderSubtle};
  border-radius: ${stoRadius.lg};
  box-shadow: 0px 4px 20px 0px rgba(0, 0, 0, 0.08);
  overflow: hidden;
`

export const PanelBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${stoSpace[6]};
  padding: 24px;
  width: 100%;

  ${MOBILE} {
    padding: ${stoSpace[4]};
  }
`

/** Label + content, stacked — the "AMOUNT" / "TRADE ON …" / "MATURITY DATE" pattern. */
export const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${stoSpace[1]};
  width: 100%;
`

/** "AMOUNT", "TRADE ON BITFINEX SECURITIES", "MATURITY DATE" — every uppercase field label. */
export const FieldLabel = styled.p`
  ${stoType.columnLabel};
  margin: 0;
  color: ${stoColor.textSecondary};
`

export const FieldHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`

export const FieldHint = styled.span`
  font-size: 11px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0.4px;
  color: ${stoColor.textTertiary};
`

/** White card with the literal Figma-flagged #e0e0e0 border — instruction/date boxes. */
export const InfoCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  width: 100%;
  padding: 12px;
  background: ${stoColor.surface};
  border: 1px solid ${stoColor.borderCard};
  border-radius: 8px;
`

export const InfoCardTitle = styled.p`
  ${stoType.bodySmTight};
  margin: 0;
  font-weight: 600;
  color: ${stoColor.textPrimary};
`

export const InfoCardText = styled.p`
  ${stoType.bodySmTight};
  margin: 0;
  color: ${stoColor.textSecondary};
`

/** The `Notice` box (Figma InfoBox): green for done or open, soft amber for something to do.
 *  A title alone sits on one line with a 16px icon (Figma 3469:35423). */
export const StatusBox = styled.div<{ $variant: 'success' | 'warning'; $compact: boolean }>`
  display: flex;
  align-items: ${({ $compact }) => ($compact ? 'center' : 'flex-start')};
  gap: ${({ $compact }) => ($compact ? stoSpace[2] : stoSpace[3])};

  > svg {
    width: ${({ $compact }) => ($compact ? '16px' : '20px')};
    height: ${({ $compact }) => ($compact ? '16px' : '20px')};
  }

  width: 100%;
  padding: 12px;
  border-radius: ${stoRadius.lg};
  background: ${({ $variant }) => ($variant === 'warning' ? stoColor.warningSurfaceSoft : stoColor.successSurface)};
`

/** The box's info icon — green for open, amber for closed (the svg fills `currentColor`). */
export const StatusIcon = styled(InfoCircleIcon)<{ $variant: 'success' | 'warning' }>`
  flex: 0 0 auto;
  width: 20px;
  height: 20px;
  color: ${({ $variant }) => ($variant === 'warning' ? stoColor.warningIcon : stoColor.successIcon)};
`

export const StatusTitle = styled.p`
  ${stoType.bodySmTight};
  margin: 0;
  font-weight: 600;
  color: ${stoColor.textPrimary};
`

export const StatusText = styled.p`
  ${stoType.bodySmTight};
  margin: 0;
  color: ${stoColor.textPrimary};
`

/** The Trade tab's legal note. */
export const Disclaimer = styled.p`
  ${stoType.disclaimer};
  margin: 0;
  width: 100%;
  color: ${stoColor.neutral500};
`

export const FooterText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  min-width: 0;
`

export const FooterMessage = styled.p`
  ${stoType.footerMessage};
  margin: 0;
  color: ${stoColor.textPrimary};
`

export const FooterDetail = styled.p`
  ${stoType.bodyLg};
  margin: 0;
  color: ${stoColor.textPrimary};
`

/**
 * Below the main Card — its own smaller card, own (lighter) shadow. In the sticky header
 * (Figma 3482:30640) the same content sits bare: right-aligned text, then the button.
 */
export const FooterCard = styled.div<{ $layout: 'card' | 'header' }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${stoSpace[5]};
  width: 100%;
  padding: 24px;
  background: ${stoColor.surface};
  border: 1px solid ${stoColor.borderSubtle};
  border-radius: ${stoRadius.lg};
  box-shadow: 0px 4px 10px 0px rgba(0, 0, 0, 0.08);

  /* Figma mobile: the action goes full width under the message. */
  ${MOBILE} {
    flex-direction: column;
    align-items: stretch;
    gap: ${stoSpace[4]};
    padding: ${stoSpace[4]};
  }

  ${({ $layout }) =>
    $layout === 'header' &&
    css`
      gap: ${stoSpace[6]};
      width: auto;
      padding: 0;
      background: none;
      border: none;
      box-shadow: none;

      /* Only the title shows here, on one line; the product name beside it gives way instead. */
      ${FooterText} {
        flex-shrink: 0;
        text-align: right;
      }

      ${FieldLabel} {
        ${stoType.statLabel};
        font-size: 11px;
        line-height: 16px;
      }

      ${FooterMessage} {
        ${stoType.bodyMdCallout};
        white-space: nowrap;
      }

      ${FooterDetail} {
        ${stoType.bodyMd};
      }
    `}
`

/**
 * The shared primary `Button`. Two overrides on top of its own styling:
 *  - `flex-shrink: 0` + `white-space: nowrap` — it has no shrink of its own, so in
 *    this narrow aside column the label wrapped onto two lines.
 *  - `color` `!important` — this renders `as={SmartLink}` for the signed-out CTA, a
 *    real `<a>`, and the global anti-leak rule in `style/base.js`
 *    (`a { color: inherit !important }`) otherwise beats the library Button's own
 *    (non-`!important`) white label color, turning it black.
 */
export const FooterAction = styled(Button)`
  flex-shrink: 0;
  white-space: nowrap;
  color: ${stoColor.buttonLabelOnFill} !important;
`

/** Wraps a disabled action so its tooltip still opens: a disabled button gets no pointer events. */
export const LockedTrigger = styled.span`
  display: block;
  width: 100%;
  cursor: not-allowed;
`

/**
 * The shared `Button` in its `black` variant (transparent, black border/label, fills
 * black on hover) — Trade's "Trade now on …" CTA. Only layout is added: full width,
 * and a flex row so the external-link icon sits beside the label.
 */
export const TradeAction = styled(Button)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;

  /* Figma disabled outline: grey border and label at full opacity, not the library's fade. */
  ${({ disabled }) =>
    disabled &&
    `
      background-color: transparent;
      border-color: ${stoColor.borderSubtle};
      color: ${stoColor.disabledText} !important;
      opacity: 1;
    `}
`

/**
 * The shared primary `Button` — the Invest and Redeem CTA. Full width. Disabled state is overridden
 * to a solid grey (the library's own disabled is a faded opacity-0.2 look); the label
 * stays white on the enabled blue via `buttonLabelOnFill`.
 */
export const PrimaryAction = styled(Button)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  /* 48px, over the library button's 40. */
  min-height: 48px;
  color: ${stoColor.buttonLabelOnFill} !important;

  ${({ disabled }) =>
    disabled &&
    `
      background-color: ${stoColor.neutral100};
      border-color: ${stoColor.neutral100};
      color: ${stoColor.disabledText} !important;
      opacity: 1;
    `}
`

/* The amount field: Invest's Amount and Securities, the Borrow calculator. */

export type FieldTone = 'editable' | 'readOnly' | 'closed'

/** Figma: white while typed in, #f8f8f8 when it follows the other field, #f0f0f0 when closed. */
const FIELD_BACKGROUND: Record<FieldTone, string> = {
  editable: 'transparent',
  readOnly: stoColor.surfaceAlt,
  closed: stoColor.neutral100,
}

export const AmountBox = styled.div<{ $tone: FieldTone }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: ${stoSpace[4]} 20px;
  border: 1px solid ${stoColor.borderSubtle};
  border-radius: ${stoRadius.lg};
  background: ${({ $tone }) => FIELD_BACKGROUND[$tone]};

  ${stoMedia.belowDesktop} {
    padding: ${stoSpace[4]};
  }
`

/** Native field styling off: iOS otherwise uses its system font, own height and dimmed disabled text. */
export const AmountInput = styled.input`
  ${stoType.amountLg};
  flex: 1;
  min-width: 0;
  height: 40px;
  padding: 0;
  font-family: inherit;
  border: none;
  border-radius: 0;
  outline: none;
  background: transparent;
  color: ${stoColor.textPrimary};
  appearance: none;

  &::placeholder {
    color: ${stoColor.textTertiary};
  }

  &:disabled,
  &:disabled::placeholder {
    color: ${stoColor.disabledText};
    -webkit-text-fill-color: ${stoColor.disabledText};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 1;
  }
`

/** Token symbols (STRCst) keep their case; currency codes are upper case anyway. */
export const CurrencyPill = styled.span<{ $muted: boolean }>`
  ${stoType.labelCta};
  flex-shrink: 0;
  padding: 6px 16px;
  border: 2px solid ${stoColor.borderSubtle};
  border-radius: ${stoRadius.full};
  text-transform: none;
  color: ${({ $muted }) => ($muted ? stoColor.disabledText : stoColor.textPrimary)};
`
